/**
 * @file
 * @fileOverview This file contains the NPC components for the Aframe scene.
 * @version 1.0.0
 * @author Parker Eli Bidigare
 * @date 2024-05-26
 */

AFRAME.registerComponent('steer-behavior', {
    schema: {
      waypoints: { type: 'array' },
      stopDistance: { type: 'number', default: 1.5 },  // Distance at which the entity stops
      hoverAmplitude: { type: 'number', default: 0.05 },  // Hover amplitude (up/down distance)
      hoverSpeed: { type: 'number', default: 2 },  // Hover speed
      speed: { type: 'number', default: 0.005 },  // Speed of the entity when moving
      turnSpeed: { type: 'number', default: 0.02 }  // Speed of the turning motion
    },
    init: function () {
      this.currentWaypointIndex = 0;
      this.waypoints = this.data.waypoints.map(id => document.querySelector(id));
      this.camera = document.querySelector('#camera');
      this.isStopped = false;
      this.originalY = this.el.object3D.position.y;  // Store original Y position for hover effect
      this.isTurning = true;  // Start by turning towards the first waypoint
    },
    tick: function (time, timeDelta) {
      const entity = this.el;
      const waypoint = this.waypoints[this.currentWaypointIndex];
      const cameraPos = new THREE.Vector3().copy(this.camera.object3D.position);
      const entityPos = new THREE.Vector3().copy(entity.object3D.position);
  
      // Calculate distance between camera and entity
      const distanceToCamera = entityPos.distanceTo(cameraPos);
  
      // If the camera is close, stop and look at the camera (back facing the camera)
      if (distanceToCamera < this.data.stopDistance) {
        this.isStopped = true;
        this.lookAtWithBackFacing(entity, cameraPos);
  
        // Apply hover effect
        const hoverY = this.originalY + Math.sin(time / 1000 * this.data.hoverSpeed) * this.data.hoverAmplitude;
        entity.object3D.position.y = hoverY;
        return;
      }
  
      // If the camera is far, resume movement
      if (this.isStopped && distanceToCamera >= this.data.stopDistance) {
        this.isStopped = false;
        entity.object3D.position.y = this.originalY;  // Reset Y position when resuming movement
      }
  
      if (!waypoint || this.isStopped) return;
  
      const waypointPos = new THREE.Vector3().copy(waypoint.object3D.position);
      const direction = waypointPos.sub(entityPos).normalize();
  
      // Rotate entity to face the waypoint (back facing the waypoint) with gradual turning
      if (this.isTurning) {
        this.isTurning = this.turnToTarget(entity, waypointPos, timeDelta);
      }
  
      // If the entity has finished turning, move it towards the waypoint
      if (!this.isTurning) {
        entity.object3D.position.add(direction.multiplyScalar(this.data.speed * timeDelta));
  
        // Check if the entity is close enough to the waypoint
        if (entityPos.distanceTo(waypoint.object3D.position) < 0.1) {
          this.currentWaypointIndex = (this.currentWaypointIndex + 1) % this.waypoints.length;
          this.isTurning = true;  // Start turning towards the next waypoint
        }
      }
    },
    lookAtWithBackFacing: function(entity, targetPos) {
      // Make the entity look at the target position
      entity.object3D.lookAt(targetPos);
  
      // Rotate the entity 180 degrees to make the back face the target
      entity.object3D.rotateY(Math.PI);
    },
    turnToTarget: function(entity, targetPos, timeDelta) {
      // Calculate the target rotation
      const targetRotation = new THREE.Quaternion();
      const currentRotation = entity.object3D.quaternion.clone();
      entity.object3D.lookAt(targetPos);
      entity.object3D.rotateY(Math.PI); // Rotate 180 degrees to have the back facing the target
      targetRotation.copy(entity.object3D.quaternion);
      entity.object3D.quaternion.copy(currentRotation); // Reset to the original rotation
  
      // Spherically interpolate between the current rotation and the target rotation
      currentRotation.slerp(targetRotation, this.data.turnSpeed * timeDelta / 1000);
      entity.object3D.quaternion.copy(currentRotation);
  
      // Check if the entity has finished turning (approximate check)
      const angleDifference = currentRotation.angleTo(targetRotation);
      if (angleDifference < 0.01) {
        // Mark turning as complete
        this.isTurning = false;
      }
  
      return this.isTurning; // Return true if still turning, false if done
    }
  });

  export const NPC = {
    SteerBehavior: AFRAME.components['steer-behavior']
  };